import { get, omit } from "underscore";
import { convertToCamelCase } from "./converters";
import { MY_LAB, SPLITTER, equipmentStatus } from "../../constants";
import { buildContainsQuery, escapeUnescapedSpaces, exactSearchString } from "./lucene";

const checkShouldKeepFetching = (drillData, items, limit, customizedItems) => {
  if (customizedItems) {
    return items.length <= customizedItems;
  }
  return drillData ? drillData : items.length < limit;
};

const createOrFilter = (arrayFilterKeys, arrayFilters) => {
  return arrayFilterKeys
    ?.reduce(
      (acc, key) =>
        arrayFilters[key].length
          ? [
              ...acc,
              arrayFilters[key]?.reduce(
                (acc_i, val) => (typeof val === "string" ? [...acc_i, getGQLFilterObj({ [key]: val })] : acc_i),
                []
              )
            ]
          : acc,
      []
    )
    .filter((arr) => arr.length);
};

export const createGQLFilterObj = (obj) => {
  if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
    return null;
  }
  const objKeys = Object.keys(obj);
  if (!objKeys.length) {
    return null;
  }
  const arrayFilters = objKeys.reduce(
    (acc, curr) => (Array.isArray(obj[curr]) ? { ...acc, [curr]: obj[curr] } : acc),
    {}
  );
  const arrayFilterKeys = Object.keys(arrayFilters);
  if (arrayFilterKeys.length) {
    const OrFilter = createOrFilter(arrayFilterKeys, arrayFilters);
    if (!OrFilter.length) {
      return getGQLFilterObj(omit(obj, arrayFilterKeys));
    }
    const simpleFilter = getGQLFilterObj(omit(obj, arrayFilterKeys));
    if (!simpleFilter) {
      return {
        and: [...OrFilter?.map((arr) => ({ or: arr }))]
      };
    }
    return {
      and: [...OrFilter?.map((arr) => ({ or: arr })), simpleFilter]
    };
  }
  return getGQLFilterObj(obj);
};

export const getGQLFilterObj = (obj) => {
  if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
    return null;
  }
  const result = Object.keys(obj).reduce((acc, cur) => {
    if (obj?.[cur] === null || obj?.[cur] === undefined) {
      return acc;
    }
    if (typeof obj?.[cur] === "string" && (obj?.[cur]?.trim() === "" || obj?.[cur]?.trim() === "all")) {
      return acc;
    }
    return { ...acc, [cur]: { eq: obj[cur] } };
  }, {});
  return Object.keys(result).length === 0 ? null : result;
};

const fetchOne = async ({
  client,
  query,
  fetchPolicy,
  variables,
  nextToken,
  dataPath,
  noItems,
  items,
  drillData,
  limit,
  customizedItems
}) => {
  const result = await client.query({
    query,
    fetchPolicy,
    variables: {
      ...variables,
      nextToken
    }
  });
  const dataPathProcessed = noItems ? dataPath : [...dataPath, "items"];
  const page = get(result, dataPathProcessed) ?? [];
  items = Array.isArray(page) ? [...items, ...page] : items;
  const _nextToken = get(result, [...dataPath, "nextToken"]);
  nextToken = typeof _nextToken === "string" && _nextToken ? _nextToken : null;
  const shouldKeepFetching = checkShouldKeepFetching(drillData, items, limit, customizedItems);
  return { nextToken, items, shouldKeepFetching };
};

export const getAllData = async ({
  client,
  query,
  fetchPolicy = "no-cache",
  variables = {},
  dataPath,
  drillData = false,
  customizedItems = false, // Loop the query for a customized item
  noItems = false
}) => {
  const emptyResponse = {
    items: [],
    nextToken: null,
    error: null
  };
  if (!client || !query || !variables) {
    return {
      ...emptyResponse,
      error: `Missing client ${client}, query ${query} or variables ${variables} `
    };
  }
  if (!Array.isArray(dataPath)) {
    return {
      ...emptyResponse,
      error: `dataPath is not an array ${dataPath}`
    };
  }
  if (dataPath.length === 0) {
    return { ...emptyResponse, error: `Empty dataPath ${dataPath}` };
  }
  try {
    let items = [];
    let nextToken = variables?.nextToken ?? null;
    let shouldKeepFetching = true;
    const { limit = 1000 } = variables;
    do {
      ({ nextToken, items, shouldKeepFetching } = await fetchOne({
        client,
        query,
        fetchPolicy,
        variables,
        nextToken,
        dataPath,
        items,
        noItems,
        shouldKeepFetching,
        drillData,
        limit,
        customizedItems
      }));
    } while (nextToken !== null && shouldKeepFetching);
    return {
      items,
      nextToken,
      error: null
    };
  } catch (err) {
    return { ...emptyResponse, error: err };
  }
};

export const getData = async ({ client, query, fetchPolicy = "no-cache", variables = {}, dataPath }) => {
  const result = await client.query({
    query: query,
    fetchPolicy,
    variables: {
      ...variables
    }
  });
  if (result.errors) {
    if (Array.isArray(result.errors)) {
      const errorsJoined = result.error.map((error) => error.message).join(", ");
      throw new Error(errorsJoined);
    }
  }
  return get(result, [...dataPath]) ?? null;
};

export const getContainerHeightPercentage = (currentEnv) => {
  return ((window.innerHeight - (currentEnv ? 110 : 70)) / window.innerHeight) * 100;
};

export const getFilterModel = (filterModel, filterName = "") => {
  let filterQuery = ``;
  for (const key in filterModel) {
    let query = ``;
    if (filterModel[key]?.values) {
      filterModel[key]?.values?.forEach((element) => {
        if (key !== "status") {
          query += ` ${key}:'${element}'`;
        }
      });
    } else if (filterModel[key]?.filterType === "date") {
      query += ` ${key}:['${filterModel[key]?.dateFrom}','${filterModel[key]?.dateTo}']`;
    } else if (filterModel[key]?.filterType === "text") {
      query += ` ${key}:'${filterModel[key]?.filter}'`;
    }
    if (key !== "status") {
      filterQuery += ` (or${query})`;
    }
  }

  if (filterName === MY_LAB) {
    filterQuery += ` (or status:'${equipmentStatus?.active?.value}' status:'${equipmentStatus?.pending?.value}')`;
  }

  if (filterQuery?.length === 0) {
    return null;
  }

  return filterQuery?.split("or")?.length > 2 ? `(and${filterQuery})` : filterQuery?.trim();
};

export const replaceDateUtil = (value, operator) => {
  let date = `${value?.replace(/ /g, "T")}Z`;
  date = date?.replace("00:00:00", "00:00:01");
  if (operator === "lessThan") {
    date = date?.replace("00:00:01", "00:00:00");
  } else if (operator === "greaterThan") {
    date = date?.replace("00:00:01", "00:00:02");
  }
  return date;
};

export const getNumberSearchQuery = (fields, searchValue, operator) => {
  let searchNumberString = [];
  fields?.forEach((field) => {
    searchNumberString.push(`(${field}:${searchValue})`);
  });

  return searchNumberString?.join(` ${operator} `);
};

export const getFilterModelQuery = ({ filterModel, type, filterName = "", masterDatas = {} }) => {
  let filterTextQuerys = [];
  const isDateTypeFilter = type === "date";

  for (const key in filterModel) {
    let query = ``;
    let queries = [];
    if (type === "set") {
      const removeIndex = filterModel[key]?.values.findIndex((item) => !item);
      let blank = null;
      if (masterDatas[convertToCamelCase(key)]?.length === filterModel[key]?.values?.length) {
        query = `(${key}:*`;

        if (removeIndex !== -1) {
          query = `${query} OR (*:* OR -${key}:*)`;
        }
        query = `${query})`;
      } else {
        if (removeIndex !== -1) {
          blank = filterModel[key]?.values?.splice(removeIndex, 1);
        }
        if (filterModel[key]?.values?.length !== 0) {
          let updatedArray = [];
          updatedArray = exactSearchString(filterModel[key]?.values, !isDateTypeFilter)?.map(
            (value) => `${key}:${value}`
          );
          query = `(${updatedArray?.join(SPLITTER.OR)})`;
        }

        if (blank) {
          query = query ? `(${query} OR (*:* OR -${key}:*))` : `(*:* OR -${key}:*)`;
        }
      }
    } else if (filterModel[key]?.operator) {
      let testConditions = [];
      let conditions = [];
      if (filterModel[key]?.conditions) {
        conditions = [...filterModel[key]?.conditions];
      } else {
        conditions = [filterModel[key]?.condition1, filterModel[key]?.condition2];
      }
      testConditions = [...conditions];
      testConditions?.forEach((condition) => {
        const isDateCondition =
          condition?.filterType === "date" || filterModel[key]?.filterType === "date" || isDateTypeFilter;
        const normalizedConditionFilter =
          typeof condition?.filter === "string" ? condition.filter.trim() : condition?.filter;
        const escapedConditionFilter =
          typeof normalizedConditionFilter === "string" && !isDateCondition
            ? escapeUnescapedSpaces(normalizedConditionFilter)
            : normalizedConditionFilter;
        if (condition?.type === "equals") {
          queries?.push(`${key}:${escapedConditionFilter}`);
        } else if (condition?.type === "contains") {
          queries?.push(buildContainsQuery(key, normalizedConditionFilter));
        } else if (condition?.type === "notContains") {
          queries?.push(`-${key}:*${escapedConditionFilter}*`);
        } else if (condition?.type === "notEqual") {
          queries?.push(`-${key}:${escapedConditionFilter}`);
        } else if (condition?.type === "lessThan" || condition?.type === "lessThanOrEqual") {
          queries?.push(`${key}:[* TO ${condition?.filter}}`);
        } else if (condition?.type === "greaterThan" || condition?.type === "greaterThanOrEqual") {
          queries?.push(`${key}:{${condition?.filter} TO *]`);
        } else if (condition?.type === "notBlank") {
          queries?.push(type === "text" ? `(*:* OR ${key}:*)` : `${key}:[* TO *]`);
        } else {
          queries?.push(type === "text" ? `(*:* OR -${key}:*)` : `-${key}:[* TO *]`);
        }
      });
      query = `(${queries?.join(` ${filterModel[key]?.operator} `)})`;
    } else {
      const isDateFilter = filterModel[key]?.filterType === "date" || isDateTypeFilter;
      const normalizedFilterValue =
        typeof filterModel[key]?.filter === "string" ? filterModel[key].filter.trim() : filterModel[key]?.filter;
      const escapedFilterValue =
        typeof normalizedFilterValue === "string" && !isDateFilter
          ? escapeUnescapedSpaces(normalizedFilterValue)
          : normalizedFilterValue;
      if (filterModel[key]?.type === "equals") {
        query =
          type === "date"
            ? `${key}:"${replaceDateUtil(filterModel[key]?.dateFrom, filterModel[key]?.type)}"`
            : `${key}:${escapedFilterValue}`;
      } else if (filterModel[key]?.type === "contains") {
        query = buildContainsQuery(key, normalizedFilterValue);
      } else if (filterModel[key]?.type === "notContains") {
        query = `-${key}:*${escapedFilterValue}*`;
      } else if (filterModel[key]?.type === "startsWith") {
        query = `${key}:${escapedFilterValue}*`;
      } else if (filterModel[key]?.type === "endsWith") {
        query = `${key}:*${escapedFilterValue}`;
      } else if (filterModel[key]?.type === "notEqual") {
        query =
          type === "date"
            ? `-${key}:"${replaceDateUtil(filterModel[key]?.dateFrom, filterModel[key]?.type)}"`
            : `-${key}:${escapedFilterValue}`;
      } else if (filterModel[key]?.type === "lessThan" || filterModel[key]?.type === "lessThanOrEqual") {
        query =
          type === "date"
            ? `${key}:[* TO ${replaceDateUtil(filterModel[key]?.dateFrom, filterModel[key]?.type)}]`
            : `${key}:[* TO ${filterModel[key]?.filter}}`;
      } else if (filterModel[key]?.type === "greaterThan" || filterModel[key]?.type === "greaterThanOrEqual") {
        query =
          type === "date"
            ? `${key}:[${replaceDateUtil(filterModel[key]?.dateFrom, filterModel[key]?.type)} TO *]`
            : `${key}:{${filterModel[key]?.filter} TO *]`;
      } else if (filterModel[key]?.type === "inRange") {
        query =
          type === "date"
            ? `${key}:[${replaceDateUtil(filterModel[key]?.dateFrom, filterModel[key]?.type)} TO ${replaceDateUtil(
                filterModel[key]?.dateTo,
                filterModel[key]?.type
              )}]`
            : `${key}:[${filterModel[key]?.filter} TO ${filterModel[key]?.filterTo}]`;
      } else if (filterModel[key]?.type === "notBlank") {
        query = type === "text" ? `(*:* OR ${key}:*)` : `${key}:[* TO *]`;
      } else {
        query = type === "text" ? `(*:* OR -${key}:*)` : `-${key}:[* TO *]`;
      }
    }
    filterTextQuerys?.push(query);
  }

  let finalStringSearchQuery =
    filterTextQuerys?.length > 1 ? filterTextQuerys?.join(SPLITTER.AND) : filterTextQuerys[0] || "";

  if (filterName === MY_LAB) {
    finalStringSearchQuery += ` AND (status:'${equipmentStatus?.active?.value}' OR status:'${equipmentStatus?.pending?.value}')`;
  }

  return finalStringSearchQuery;
};
